/**
 * `/components`: the single component gallery (Fase D, D11 + D28).
 *
 * Every component the platform draws with, in one place and inside the
 * builder's own shell: the kit (@omnitok/ui v3), bi-components (its
 * manifest), dashboard-render and admin-ui. A list of categories on the
 * left, search, and one card per component with its live demo, optional
 * interaction, the data a chart plots and the import line to copy.
 *
 * Ported from the review bench approved on 2026-10-24
 * (`omnitok-ui-prototype/src/kit/KitApp.jsx`); the look lives in Tailwind
 * classes here, not in a stylesheet (D12).
 */
import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ChevronRight, Copy, Layers, Palette, RotateCcw, Search, X } from "lucide-react";

import { I18nProvider, ToastProvider } from "@omnitok/ui";
import { ChartDataExplorer, MotionConfig } from "@pervasivemind/bi-components";
import { PageContainer, PageHeader } from "@pervasivemind/admin-ui";

import { useT } from "@/i18n";

import { CATEGORIES, CATEGORY_IDS, GALLERY_ENTRIES, ONE_COLUMN, chartRows, importLine, type GalleryEntry } from "./catalog";
import { KIT_MISSING } from "./kit-demos";

type T = ReturnType<typeof useT>["t"];

const UNIQUE_NAMES = new Set(GALLERY_ENTRIES.flatMap((e) => e.covers ?? [e.componentName]));

class DemoBoundary extends Component<{ children: ReactNode; label: (message: string) => string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    // Fail loud, scoped to the card: one broken demo never blanks the page.
    return this.state.error ? (
      <div role="alert" data-gallery-demo-error="" className="rounded-xl border border-error/30 bg-error-soft p-5 text-xs text-error-dark">
        {this.props.label(this.state.error.message)}
      </div>
    ) : (
      this.props.children
    );
  }
}

function DemoCard({ entry, replay, t, locale }: { entry: GalleryEntry; replay: number; t: T; locale: string }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [interactive, setInteractive] = useState(true);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    innerRef.current?.toggleAttribute("inert", !interactive);
  }, [interactive]);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)));
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);
  const height = entry.fullHeight ?? (["kpis", "filters", "dates", "tables", "transitions", "brand"].includes(entry.category) ? 260 : 250);
  const node = entry.render(height, width, locale);
  const rows = entry.category === "charts" ? chartRows(node) : [];
  const sourceLabel = t(`gallery.source.${entry.source}`);
  async function copyImport() {
    await navigator.clipboard.writeText(importLine(entry));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }
  // Interactive cards do not clip: the calendar, dropdown and menu demos
  // open their panel in place, and `overflow-hidden` cut it at the card
  // edge so the lower days could not be clicked (QA look v3 F13). The
  // card that holds the focus rises over its neighbours.
  return (
    <article
      id={entry.id}
      data-gallery-card={entry.id}
      data-source={entry.source}
      className={`min-w-0 rounded-[14px] border border-neutral-200 bg-white shadow-card ${interactive ? "relative focus-within:z-20" : "overflow-hidden"}`}
    >
      <header className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
        <div className="min-w-0">
          <h2 className="font-display text-[15px] font-extrabold text-neutral-900">{entry.title}</h2>
          {entry.subtitle && <p className="mt-0.5 max-w-[70ch] text-xs leading-relaxed text-neutral-500">{entry.subtitle}</p>}
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-neutral-200 px-2 py-0.5 text-2xs font-semibold text-neutral-600">
          <i aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary" />
          {entry.composition ? t("gallery.composition") : t("gallery.component")}
        </span>
      </header>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-5 py-2 text-2xs text-neutral-500">
        <span>{entry.composition ? t("gallery.composedOf", { parts: entry.composition.join(" + ") }) : t("gallery.preview")}</span>
        <button
          type="button"
          role="switch"
          aria-checked={interactive}
          aria-label={t("gallery.interactive.aria", { title: entry.title })}
          onClick={() => setInteractive((v) => !v)}
          className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 font-semibold text-neutral-700 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span aria-hidden="true" className={`relative h-4 w-7 rounded-full transition-colors ${interactive ? "bg-primary" : "bg-neutral-300"}`}>
            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] ${interactive ? "left-3.5" : "left-0.5"}`} />
          </span>
          {interactive ? t("gallery.interactive.on") : t("gallery.interactive.off")}
        </button>
      </div>
      <div ref={stageRef} className="border-t border-neutral-200 px-5 py-5">
        <div ref={innerRef} className={interactive ? undefined : "pointer-events-none select-none"}>
          <DemoBoundary key={`${entry.id}-${replay}`} label={(message) => t("gallery.demoError", { message })}>
            <ChartDataExplorer rows={rows} interactive={interactive} label={t("gallery.exploreData")} emptyLabel={t("gallery.noData")}>
              {node}
            </ChartDataExplorer>
          </DemoBoundary>
        </div>
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-5 py-2.5 text-2xs text-neutral-500">
        <span className="flex min-w-0 items-center gap-2">
          <code className="truncate rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-neutral-700">{(entry.covers ?? [entry.componentName]).join(" · ")}</code>
          <button
            type="button"
            onClick={() => void copyImport()}
            className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 font-semibold text-primary hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
            {copied ? t("gallery.copied") : t("gallery.copyImport")}
          </button>
        </span>
        <span>{sourceLabel}</span>
      </footer>
    </article>
  );
}

// The kit's own variables (@omnitok/ui styles.css): the swatch paints the
// live token and names it, so the gallery cannot drift from the kit.
const SWATCHES: ReadonlyArray<[string, string, string]> = [
  ["Ink", "--omni-navy", "ink"],
  ["Pink", "--omni-pink", "pink"],
  ["Violet", "--omni-violet", "violet"],
  ["Module", "--omni-selected", "module"],
  ["Canvas", "--omni-canvas", "canvas"],
  ["Border", "--omni-line", "border"],
];
// Module identity colours of the reference: the kit has no token for
// them yet (finding of look v3 Fase 1), so they stay as written.
const MODULES: ReadonlyArray<[string, string, string]> = [
  ["Pricing", "#E6136F", "#FFE8F2"], // color-gate: allow module identity colour, no kit token yet
  ["Share of Search", "#5B55C7", "#EEEDFA"], // color-gate: allow module identity colour, no kit token yet
  ["Disponibilidad", "#0B7A5F", "#E6F7F1"], // color-gate: allow module identity colour, no kit token yet
  ["ePerfect Store", "#8E44AD", "#F4EAF8"], // color-gate: allow module identity colour, no kit token yet
];

function Foundations({ t, replay, locale }: { t: T; replay: number; locale: string }) {
  const inContext = ["ui-report-tabs", "ui-button", "ui-cards"]
    .map((id) => GALLERY_ENTRIES.find((e) => e.id === id))
    .filter((e): e is GalleryEntry => e != null);
  const card = "rounded-[14px] border border-neutral-200 bg-white p-5 shadow-card";
  return (
    <div className="flex flex-col gap-4">
      <section className={card}>
        <h2 className="font-display text-[15px] font-extrabold text-neutral-900">{t("gallery.foundation.palette.title")}</h2>
        <p className="mt-0.5 text-xs text-neutral-500">{t("gallery.foundation.palette.text")}</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {SWATCHES.map(([name, color, usage]) => (
            <div key={name} className="min-w-0">
              <div className="h-14 rounded-xl border border-neutral-200" style={{ background: `var(${color})` }} />
              <b className="mt-2 block text-xs text-neutral-800">{name}</b>
              <code className="text-2xs text-neutral-500">{color}</code>
              <small className="block text-2xs text-neutral-500">{t(`gallery.foundation.usage.${usage}`)}</small>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {MODULES.map(([name, color, bg]) => (
            <span key={name} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-semibold" style={{ color, background: bg }}>
              <i aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
              {name}
            </span>
          ))}
        </div>
      </section>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className={card}>
          <h2 className="font-display text-[15px] font-extrabold text-neutral-900">{t("gallery.foundation.type.title")}</h2>
          <p className="mt-3 font-display text-[30px] font-extrabold leading-tight tracking-[-0.6px] text-neutral-900">{t("gallery.foundation.type.sample")}</p>
          <p className="mt-1 font-display text-2xl font-extrabold tabular-nums text-positive">89,3 % <small className="text-xs font-bold">▲ 2,4 pp</small></p>
          <p className="mt-2 max-w-[70ch] text-xs leading-relaxed text-neutral-600">{t("gallery.foundation.type.text")}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-2xs text-neutral-500">
            <span>Inter · 11 / 12 / 13 / 14</span>
            <span>Nunito Sans · 15 / 25 / 30</span>
          </div>
        </section>
        <section className={card}>
          <h2 className="font-display text-[15px] font-extrabold text-neutral-900">{t("gallery.foundation.surfaces.title")}</h2>
          <div className="mt-4 flex flex-wrap gap-5">
            {([[10, "field"], [12, "control"], [14, "card"], [18, "panel"]] as const).map(([radius, key]) => (
              <div key={key} className="text-center">
                <i aria-hidden="true" className="block h-12 w-16 border border-neutral-300 bg-neutral-50" style={{ borderRadius: radius }} />
                <b className="mt-1.5 block text-xs text-neutral-800">{radius}px</b>
                <small className="text-2xs text-neutral-500">{t(`gallery.foundation.radius.${key}`)}</small>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-end gap-4">
            {[4, 8, 12, 16, 24, 32].map((n) => (
              <div key={n} className="flex flex-col items-center gap-1">
                <i aria-hidden="true" className="block rounded-sm bg-primary/70" style={{ width: n, height: n }} />
                <span className="text-2xs text-neutral-500">{n}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <h2 className="mt-2 font-display text-[15px] font-extrabold text-neutral-900">{t("gallery.foundation.inContext")}</h2>
      {inContext.map((entry) => (
        <DemoCard key={entry.id} entry={entry} replay={replay} t={t} locale={locale} />
      ))}
    </div>
  );
}

export function GalleryPage(): JSX.Element {
  const { t, locale } = useT();
  const [params, setParams] = useSearchParams();
  const requested = params.get("category") ?? "foundation";
  // An unknown category in the URL is a visible defect, never the first one.
  const category = CATEGORY_IDS.has(requested) ? requested : null;
  const selected = params.get("component");
  const [query, setQuery] = useState("");
  const [replay, setReplay] = useState(0);
  const [motion, setMotion] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(category);

  const entries = useMemo(() => {
    const q = query.toLowerCase().trim();
    return GALLERY_ENTRIES.filter((e) =>
      q
        ? [e.title, e.subtitle ?? "", e.componentName, ...(e.covers ?? [])].join(" ").toLowerCase().includes(q)
        : e.category === category && (!selected || e.id === selected),
    );
  }, [category, query, selected]);

  function navigate(id: string, component: string | null = null) {
    const next = new URLSearchParams(params);
    next.set("category", id);
    if (component) next.set("component", component);
    else next.delete("component");
    setParams(next);
    setExpanded(id);
    setQuery("");
    window.scrollTo({ top: 0 });
  }

  const label = (id: string) => t(`gallery.category.${id}`);
  const title = query ? t("gallery.searchResults") : category ? (selected ? GALLERY_ENTRIES.find((e) => e.id === selected)?.title ?? label(category) : label(category)) : requested;

  return (
    <I18nProvider language={locale}>
      <ToastProvider position="bottom-right">
        <MotionConfig reducedMotion={motion ? null : true}>
          <PageContainer wide>
            <PageHeader
              title={t("gallery.title")}
              subtitle={t("gallery.subtitle")}
              actions={
                <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                  <Layers size={14} aria-hidden="true" />
                  {t("gallery.count", { entries: GALLERY_ENTRIES.length, components: UNIQUE_NAMES.size })}
                </span>
              }
            />
            <div className="grid gap-6 lg:grid-cols-[232px_minmax(0,1fr)]">
              <nav aria-label={t("gallery.categoriesNav")} className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto">
                {CATEGORIES.map(({ id, group }, index) => {
                  const items = GALLERY_ENTRIES.filter((e) => e.category === id);
                  const current = category === id && !query && !selected;
                  return (
                    <div key={id}>
                      {group && <p className={`mb-1.5 px-2.5 text-2xs font-semibold uppercase tracking-[0.08em] text-neutral-500 ${index === 0 ? "" : "mt-5"}`}>{t(`gallery.group.${group}`)}</p>}
                      <div className="flex items-center">
                        <button
                          type="button"
                          data-category={id}
                          aria-current={current ? "page" : undefined}
                          onClick={() => navigate(id)}
                          className={`flex min-h-8 flex-1 items-center justify-between gap-2 rounded-lg px-2.5 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${current ? "bg-primary-soft font-semibold text-primary-ink" : "text-neutral-700 hover:bg-neutral-100"}`}
                        >
                          <span className="truncate">{label(id)}</span>
                          {id === "foundation" ? (
                            <Palette size={12} aria-hidden="true" />
                          ) : (
                            <em className="min-w-[22px] rounded-md border border-neutral-200 text-center text-2xs not-italic tabular-nums text-neutral-500">{items.length}</em>
                          )}
                        </button>
                        {items.length > 0 && (
                          <button
                            type="button"
                            aria-label={t("gallery.expand", { label: label(id) })}
                            aria-expanded={expanded === id}
                            onClick={() => setExpanded(expanded === id ? null : id)}
                            className="grid h-8 w-7 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          >
                            <ChevronRight size={12} aria-hidden="true" className={`transition-transform ${expanded === id ? "rotate-90" : ""}`} />
                          </button>
                        )}
                      </div>
                      {expanded === id && items.length > 0 && (
                        <div className="mb-1 ml-3 border-l border-neutral-200 pl-2">
                          {items.map((entry) => (
                            <button
                              key={entry.id}
                              type="button"
                              data-component={entry.id}
                              aria-current={selected === entry.id && !query ? "page" : undefined}
                              onClick={() => navigate(id, entry.id)}
                              className={`block w-full truncate rounded-md px-2 py-1 text-left text-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${selected === entry.id && !query ? "font-semibold text-primary-ink" : "text-neutral-600 hover:bg-neutral-100"}`}
                            >
                              {entry.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              <main className="min-w-0">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="relative flex min-w-[240px] flex-1 items-center">
                    <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-3 text-neutral-500" />
                    <input
                      id="gallery-search"
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("gallery.search")}
                      aria-label={t("gallery.search")}
                      className="h-9 w-full rounded-[10px] border border-control bg-white pl-9 pr-9 text-[13px] text-neutral-800 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    {query && (
                      <button type="button" onClick={() => setQuery("")} aria-label={t("gallery.clearSearch")} className="absolute right-2 grid h-6 w-6 place-items-center rounded-md text-neutral-500 hover:bg-neutral-100">
                        <X size={14} aria-hidden="true" />
                      </button>
                    )}
                  </label>
                  <button
                    type="button"
                    aria-pressed={motion}
                    onClick={() => setMotion((v) => !v)}
                    className="inline-flex h-9 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <span aria-hidden="true" className={`relative h-4 w-7 rounded-full transition-colors ${motion ? "bg-primary" : "bg-neutral-300"}`}>
                      <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-[left] ${motion ? "left-3.5" : "left-0.5"}`} />
                    </span>
                    {t("gallery.animations")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReplay((r) => r + 1)}
                    className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <RotateCcw size={13} aria-hidden="true" />
                    {t("gallery.replay")}
                  </button>
                </div>

                {KIT_MISSING.length > 0 && (
                  <p role="alert" data-gallery-demo-error="" className="mb-4 rounded-xl border border-error/30 bg-error-soft p-4 text-xs text-error-dark">
                    {t("gallery.missing", { names: KIT_MISSING.join(", ") })}
                  </p>
                )}

                <h2 className="mb-3 font-display text-[25px] font-extrabold leading-[1.25] tracking-[-0.5px] text-neutral-800">{title}</h2>

                {category === null && !query ? (
                  <p role="alert" data-gallery-demo-error="" className="rounded-xl border border-error/30 bg-error-soft p-4 text-xs text-error-dark">
                    {t("gallery.unknownCategory", { category: requested })}
                  </p>
                ) : category === "foundation" && !query ? (
                  <Foundations t={t} replay={replay} locale={locale} />
                ) : (
                  <>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-2xs text-neutral-500">
                      {selected && !query ? (
                        <button type="button" onClick={() => navigate(category ?? "foundation")} className="font-semibold text-primary hover:underline">
                          {t("gallery.backToFamily")}
                        </button>
                      ) : (
                        <span>{t("gallery.examples", { count: entries.length })}</span>
                      )}
                      <span>{t("gallery.sampleData")}</span>
                    </div>
                    <div
                      data-gallery-grid=""
                      className={`grid gap-4 ${category && ONE_COLUMN.has(category) || query || selected ? "grid-cols-1" : "grid-cols-1 2xl:grid-cols-2"}`}
                    >
                      {entries.map((entry) => (
                        <DemoCard key={entry.id} entry={entry} replay={replay} t={t} locale={locale} />
                      ))}
                    </div>
                    {entries.length === 0 && (
                      <div className="rounded-[14px] border border-neutral-200 bg-white px-6 py-12 text-center">
                        <Search size={24} aria-hidden="true" className="mx-auto text-neutral-500" />
                        <h3 className="mt-3 font-display text-[15px] font-extrabold text-neutral-900">{t("gallery.empty.title")}</h3>
                        <p className="mt-1 text-xs text-neutral-500">{t("gallery.empty.hint")}</p>
                        <button type="button" onClick={() => setQuery("")} className="mt-4 text-xs font-semibold text-primary hover:underline">
                          {t("gallery.clearSearch")}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </main>
            </div>
          </PageContainer>
        </MotionConfig>
      </ToastProvider>
    </I18nProvider>
  );
}
